/**
 * The auth module's single entry point.
 *
 * Everything Entra-shaped is confined to this directory: the MSAL wrapper
 * (`msal.ts`), its local stand-in (`msal.mock.ts`), the error vocabulary
 * (`errors.ts`) and the session store (`session.ts`). Nothing outside imports
 * `@azure/msal-browser`.
 */
import type { App } from 'vue'

import { registerAuthFailureHandler, registerTokenProvider } from '../api/client.js'
import { router } from '../router.js'
import { useSession } from './session.js'

export { useSession } from './session.js'
export { requireSession } from './guard.js'
export { AuthError } from './errors.js'
export type { AuthErrorCode } from './errors.js'
export type { AuthenticatedUser, UserRole } from './types.js'

/**
 * Wires the session into the API client and resolves it against MSAL.
 *
 * `main.ts` awaits this BEFORE installing the router, which is the whole
 * point: the navigation guard reads `isAuthenticated` synchronously, so if the
 * session were still resolving a hard refresh would redirect an authenticated
 * user to the login page and lose the URL they typed.
 */
export async function installAuth(_app: App): Promise<void> {
  // The MSW worker must be intercepting before the first request, and the
  // first request is `GET /api/me` inside `restore()` below. There is no
  // earlier awaited hook in the frozen bootstrap, so it starts here.
  if (import.meta.env.VITE_API_MOCK === 'true') {
    // Dynamic: the mock handlers must not exist in a production bundle, and
    // `import.meta.env` is inlined at build time so this branch is dropped.
    const { startApiMocks } = await import('../mocks/index.js')
    await startApiMocks()
  }

  // Pinia is installed by `main.ts` immediately before this call, so its
  // active instance is set and `useSession()` resolves against it. The `App`
  // handle is part of the signature the frozen `main.ts` calls, and is kept
  // even though nothing here needs it — the ordering, not the handle, is what
  // matters.
  const session = useSession()

  registerTokenProvider(() => session.getAccessToken())
  registerAuthFailureHandler(() => {
    void session.handleAuthFailure().then(() => {
      // A 401 that survives one renewal attempt has ended the session. The
      // route guard only runs on navigation, so without this the user would
      // keep staring at a page whose data can no longer load.
      if (session.isAuthenticated) return
      const current = router.currentRoute.value
      if (current.meta.public === true) return
      void router.replace({ name: 'login', query: { redirect: current.fullPath } })
    })
  })

  await session.restore()
}
